/*
 * Cookiebot consent withdrawal handler.
 *
 * Tracking scripts loaded through GTM (FB Pixel, GA, Hotjar, etc.) can set
 * first- and third-party cookies we cannot delete from concertgebouw.nl JS once
 * they are running. To guarantee a clean state when a user revokes consent we
 * trigger a hard reload. Cookiebot then runs its cookie-deletion routine on the
 * fresh page and the disallowed scripts are simply not re-injected by GTM.
 * [DESK-2969]
 */
declare global {
  interface Window {
    Cookiebot?: {
      consent?: {
        preferences?: boolean
        statistics?: boolean
        marketing?: boolean
      }
    }
  }
}

export default defineNuxtPlugin(() => {
  const CATEGORIES = ['preferences', 'statistics', 'marketing'] as const
  // Last known consent for this page load. Updated after every change that does
  // not reload, so a later revoke is compared against what the user last
  // granted — not the first snapshot on load (which may still be all-false).
  let previousConsent: Record<string, boolean> | null = null

  // Fires on every page load once Cookiebot has determined consent state, and
  // again whenever the user changes their consent via the renew-consent banner.
  window.addEventListener('CookiebotOnConsentReady', () => {
    const cb = window.Cookiebot
    if (!cb || !cb.consent) return

    const current = {
      preferences: !!cb.consent.preferences,
      statistics: !!cb.consent.statistics,
      marketing: !!cb.consent.marketing,
    }

    if (previousConsent === null) {
      previousConsent = current
      console.info('[cookiebot-consent-withdrawal] initial consent registered:', previousConsent)
      return
    }

    console.info('[cookiebot-consent-withdrawal] consent changed:', { from: previousConsent, to: current })

    // Reload only when a previously-granted category is now denied. New grants
    // don't need a reload because GTM will simply inject the newly-allowed tags.
    const withdrawn = CATEGORIES.some((c) => previousConsent![c] && !current[c])
    if (withdrawn) {
      console.info('[cookiebot-consent-withdrawal] consent withdrawn, reloading page')
      window.location.reload()
      return
    }

    console.info('[cookiebot-consent-withdrawal] no withdrawal detected, not reloading')
    previousConsent = current
  })
})
